/**
 * Language-specific Validation Rulesets
 *
 * Defines validation rules for different languages with localized error messages
 * and language-specific validation logic (e.g., character sets, formats).
 */

import i18next from 'i18next';
import { ValidationRuleset, commonValidators, ValidationContext } from '../services/validation';

/**
 * Helper function to create a translation function for validation error messages
 */
function t(key: string): (context: ValidationContext) => string {
  return (context: ValidationContext) => {
    // Use the language from the validation context
    return i18next.t(key, { ns: 'signup', lng: context.language });
  };
}

/**
 * English Validation Rules
 */
export const enValidation: ValidationRuleset = {
  language: 'en',
  rules: {
    firstName: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.firstNameRequired'),
      },
      {
        validate: commonValidators.minLength(2),
        errorMessage: t('validation.firstNameMinLength'),
      },
      {
        validate: commonValidators.maxLength(40),
        errorMessage: t('validation.firstNameMaxLength'),
      },
      {
        validate: commonValidators.latinExtended,
        errorMessage: t('validation.firstNameInvalidCharacters'),
      },
    ],
    lastName: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.lastNameRequired'),
      },
      {
        validate: commonValidators.minLength(2),
        errorMessage: t('validation.lastNameMinLength'),
      },
      {
        validate: commonValidators.maxLength(40),
        errorMessage: t('validation.lastNameMaxLength'),
      },
      {
        validate: commonValidators.latinExtended,
        errorMessage: t('validation.lastNameInvalidCharacters'),
      },
    ],
    email: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.emailRequired'),
      },
      {
        validate: commonValidators.maxLength(254),
        errorMessage: t('validation.emailMaxLength'),
      },
      {
        validate: commonValidators.email,
        errorMessage: t('validation.emailInvalid'),
      },
    ],
    country: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.countryRequired'),
      },
    ],
    marketingConsent: [
      {
        validate: (value) => value === 'yes' || value === 'no',
        errorMessage: t('validation.consentRequired'),
      },
    ],
  },
};

/**
 * French Validation Rules
 */
export const frValidation: ValidationRuleset = {
  language: 'fr',
  rules: {
    firstName: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.firstNameRequired'),
      },
      {
        validate: commonValidators.minLength(2),
        errorMessage: t('validation.firstNameMinLength'),
      },
      {
        validate: commonValidators.maxLength(40),
        errorMessage: t('validation.firstNameMaxLength'),
      },
      {
        validate: commonValidators.latinExtended,
        errorMessage: t('validation.firstNameInvalidCharacters'),
      },
    ],
    lastName: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.lastNameRequired'),
      },
      {
        validate: commonValidators.minLength(2),
        errorMessage: t('validation.lastNameMinLength'),
      },
      {
        validate: commonValidators.maxLength(40),
        errorMessage: t('validation.lastNameMaxLength'),
      },
      {
        validate: commonValidators.latinExtended,
        errorMessage: t('validation.lastNameInvalidCharacters'),
      },
    ],
    email: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.emailRequired'),
      },
      {
        validate: commonValidators.maxLength(254),
        errorMessage: t('validation.emailMaxLength'),
      },
      {
        validate: commonValidators.email,
        errorMessage: t('validation.emailInvalid'),
      },
    ],
    country: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.countryRequired'),
      },
    ],
    marketingConsent: [
      {
        validate: (value) => value === 'yes' || value === 'no',
        errorMessage: t('validation.consentRequired'),
      },
    ],
  },
};

/**
 * Spanish Validation Rules
 */
export const esValidation: ValidationRuleset = {
  language: 'es',
  rules: {
    firstName: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.firstNameRequired'),
      },
      {
        validate: commonValidators.minLength(2),
        errorMessage: t('validation.firstNameMinLength'),
      },
      {
        validate: commonValidators.maxLength(40),
        errorMessage: t('validation.firstNameMaxLength'),
      },
      {
        validate: commonValidators.latinExtended,
        errorMessage: t('validation.firstNameInvalidCharacters'),
      },
    ],
    lastName: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.lastNameRequired'),
      },
      {
        validate: commonValidators.minLength(2),
        errorMessage: t('validation.lastNameMinLength'),
      },
      {
        validate: commonValidators.maxLength(40),
        errorMessage: t('validation.lastNameMaxLength'),
      },
      {
        validate: commonValidators.latinExtended,
        errorMessage: t('validation.lastNameInvalidCharacters'),
      },
    ],
    email: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.emailRequired'),
      },
      {
        validate: commonValidators.maxLength(254),
        errorMessage: t('validation.emailMaxLength'),
      },
      {
        validate: commonValidators.email,
        errorMessage: t('validation.emailInvalid'),
      },
    ],
    country: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.countryRequired'),
      },
    ],
    marketingConsent: [
      {
        validate: (value) => value === 'yes' || value === 'no',
        errorMessage: t('validation.consentRequired'),
      },
    ],
  },
};

/**
 * Italian Validation Rules
 */
export const itValidation: ValidationRuleset = {
  language: 'it',
  rules: {
    firstName: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.firstNameRequired'),
      },
      {
        validate: commonValidators.minLength(2),
        errorMessage: t('validation.firstNameMinLength'),
      },
      {
        validate: commonValidators.maxLength(40),
        errorMessage: t('validation.firstNameMaxLength'),
      },
      {
        validate: commonValidators.latinExtended,
        errorMessage: t('validation.firstNameInvalidCharacters'),
      },
    ],
    lastName: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.lastNameRequired'),
      },
      {
        validate: commonValidators.minLength(2),
        errorMessage: t('validation.lastNameMinLength'),
      },
      {
        validate: commonValidators.maxLength(40),
        errorMessage: t('validation.lastNameMaxLength'),
      },
      {
        validate: commonValidators.latinExtended,
        errorMessage: t('validation.lastNameInvalidCharacters'),
      },
    ],
    email: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.emailRequired'),
      },
      {
        validate: commonValidators.maxLength(254),
        errorMessage: t('validation.emailMaxLength'),
      },
      {
        validate: commonValidators.email,
        errorMessage: t('validation.emailInvalid'),
      },
    ],
    country: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.countryRequired'),
      },
    ],
    marketingConsent: [
      {
        validate: (value) => value === 'yes' || value === 'no',
        errorMessage: t('validation.consentRequired'),
      },
    ],
  },
};

/**
 * Dutch Validation Rules
 */
export const nlValidation: ValidationRuleset = {
  language: 'nl',
  rules: {
    firstName: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.firstNameRequired'),
      },
      {
        validate: commonValidators.minLength(2),
        errorMessage: t('validation.firstNameMinLength'),
      },
      {
        validate: commonValidators.maxLength(40),
        errorMessage: t('validation.firstNameMaxLength'),
      },
      {
        validate: commonValidators.latinExtended,
        errorMessage: t('validation.firstNameInvalidCharacters'),
      },
    ],
    lastName: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.lastNameRequired'),
      },
      {
        validate: commonValidators.minLength(2),
        errorMessage: t('validation.lastNameMinLength'),
      },
      {
        validate: commonValidators.maxLength(40),
        errorMessage: t('validation.lastNameMaxLength'),
      },
      {
        validate: commonValidators.latinExtended,
        errorMessage: t('validation.lastNameInvalidCharacters'),
      },
    ],
    email: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.emailRequired'),
      },
      {
        validate: commonValidators.maxLength(254),
        errorMessage: t('validation.emailMaxLength'),
      },
      {
        validate: commonValidators.email,
        errorMessage: t('validation.emailInvalid'),
      },
    ],
    country: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.countryRequired'),
      },
    ],
    marketingConsent: [
      {
        validate: (value) => value === 'yes' || value === 'no',
        errorMessage: t('validation.consentRequired'),
      },
    ],
  },
};

/**
 * Polish Validation Rules
 */
export const plValidation: ValidationRuleset = {
  language: 'pl',
  rules: {
    firstName: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.firstNameRequired'),
      },
      {
        validate: commonValidators.minLength(2),
        errorMessage: t('validation.firstNameMinLength'),
      },
      {
        validate: commonValidators.maxLength(40),
        errorMessage: t('validation.firstNameMaxLength'),
      },
      {
        validate: commonValidators.latinExtended,
        errorMessage: t('validation.firstNameInvalidCharacters'),
      },
    ],
    lastName: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.lastNameRequired'),
      },
      {
        validate: commonValidators.minLength(2),
        errorMessage: t('validation.lastNameMinLength'),
      },
      {
        validate: commonValidators.maxLength(40),
        errorMessage: t('validation.lastNameMaxLength'),
      },
      {
        validate: commonValidators.latinExtended,
        errorMessage: t('validation.lastNameInvalidCharacters'),
      },
    ],
    email: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.emailRequired'),
      },
      {
        validate: commonValidators.maxLength(254),
        errorMessage: t('validation.emailMaxLength'),
      },
      {
        validate: commonValidators.email,
        errorMessage: t('validation.emailInvalid'),
      },
    ],
    country: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.countryRequired'),
      },
    ],
    marketingConsent: [
      {
        validate: (value) => value === 'yes' || value === 'no',
        errorMessage: t('validation.consentRequired'),
      },
    ],
  },
};

/**
 * Czech Validation Rules
 */
export const csValidation: ValidationRuleset = {
  language: 'cs',
  rules: {
    firstName: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.firstNameRequired'),
      },
      {
        validate: commonValidators.minLength(2),
        errorMessage: t('validation.firstNameMinLength'),
      },
      {
        validate: commonValidators.maxLength(40),
        errorMessage: t('validation.firstNameMaxLength'),
      },
      {
        validate: commonValidators.latinExtended,
        errorMessage: t('validation.firstNameInvalidCharacters'),
      },
    ],
    lastName: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.lastNameRequired'),
      },
      {
        validate: commonValidators.minLength(2),
        errorMessage: t('validation.lastNameMinLength'),
      },
      {
        validate: commonValidators.maxLength(40),
        errorMessage: t('validation.lastNameMaxLength'),
      },
      {
        validate: commonValidators.latinExtended,
        errorMessage: t('validation.lastNameInvalidCharacters'),
      },
    ],
    email: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.emailRequired'),
      },
      {
        validate: commonValidators.maxLength(254),
        errorMessage: t('validation.emailMaxLength'),
      },
      {
        validate: commonValidators.email,
        errorMessage: t('validation.emailInvalid'),
      },
    ],
    country: [
      {
        validate: commonValidators.required,
        errorMessage: t('validation.countryRequired'),
      },
    ],
    marketingConsent: [
      {
        validate: (value) => value === 'yes' || value === 'no',
        errorMessage: t('validation.consentRequired'),
      },
    ],
  },
};

/**
 * Map of language codes to validation rulesets
 */
export const validationRulesets: Record<string, ValidationRuleset> = {
  en: enValidation,
  fr: frValidation,
  es: esValidation,
  it: itValidation,
  nl: nlValidation,
  pl: plValidation,
  cs: csValidation,
};

/**
 * Gets the validation ruleset for a given language
 * Falls back to English if language not found
 */
export function getValidationRuleset(language: string): ValidationRuleset {
  return validationRulesets[language] || enValidation;
}

